import { ChangeEvent, useCallback, useRef } from 'react';
import { OTPInput, SlotProps } from 'input-otp';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useDebounce from 'hooks/useDebounce';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import TextInputEvent from 'hooks/context/EventCollector/events/TextInputEvent';
import Slot from './Slot';
import { ONLY_NUMBERS_REGEXP } from '../../constants';

export type Props = {
  value: string;
  length?: number;
  onChange?: (newValue: string) => void;
  // onInput is used when you need event, e.g: handleChange from formik
  onInput?: (e: ChangeEvent<HTMLInputElement>) => void;
  dataTest?: string;
  name: string;
  isError?: boolean;
  disabled?: boolean;
  className?: string;
  pattern?: string;
};

function OTP({
  value,
  onChange,
  onInput,
  length = 4,
  name,
  isError,
  eventCollector,
  dataTest = 'otp',
  disabled = false,
  pattern = ONLY_NUMBERS_REGEXP, // default only-numeric characters allowed
}: Props & EventCollectorProps): JSX.Element {
  const { track } = useEventCollector();
  const valueRef = useRef(value);

  const handleEventCollector = useCallback(
    (newValue: string) => {
      if (eventCollector?.dontTrack || valueRef.current === newValue) {
        return;
      }

      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? newValue,
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = newValue;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      track,
    ],
  );

  useDebounce(() => handleEventCollector(value), 1000, [value]);

  const renderSlots = (slots: SlotProps[]) => {
    return (
      <div className="flex w-full gap-2 sm:gap-4">
        {slots.map((slot, idx) => (
          <div key={idx} className="flex-1 min-w-0">
            <Slot {...slot} isError={isError} />
          </div>
        ))}
      </div>
    );
  };

  // TODO: remove OPT-INPUT library
  return (
    <OTPInput
      value={value}
      name={name}
      onInput={
        onInput
          ? (e) => onInput(e as unknown as ChangeEvent<HTMLInputElement>)
          : undefined
      }
      onChange={onChange}
      maxLength={length}
      data-test={dataTest}
      inputMode="numeric"
      render={({ slots }) => renderSlots(slots)}
      disabled={disabled}
      pattern={pattern}
    />
  );
}

export default OTP;
